import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Fade,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  ChevronLeft,
  Users,
  GraduationCap,
  TrendingUp,
  Bell,
  Star,
  BookOpen,
  MessageSquare,
  Calendar,
  BookMarked,
  Target,
  PenTool,
  FileText,
  PlusCircle,
  Menu as MenuIcon,
  X,
  FileEdit
} from 'lucide-react';

// Importar productos
import DirectiveAI from './products/DirectiveAI';
import AGTAcademic from './products/AGTAcademic';
import AGTGrowth from './products/AGTGrowth';

// Importar componentes
import ChatInputMUI from './products/ChatInputMUI';
import ChatSidebar from './components/ChatSidebar';
import MarkdownRenderer from '../AGTChat/messages/MarkdownRenderer';
import ThinkingIcon from './components/ThinkingIcon';

// Importar servicios
import agtEduService from './services/agtEduService';

// IMPORTAR NUEVO COMPONENTE PARA EDICIÓN DE DOCUMENTOS
import DocumentEditorView from './components/DocumentEditorView';

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// ====================== Estilos y componentes base ======================
const AppContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100vh',
  width: '100vw',
  overflow: 'hidden',
  position: 'relative', // Importante: establecer como relativo explícitamente
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000'
}));

// Overlay que aparece detrás del sidebar solo en móviles
const MobileOverlay = styled(Box)(({ theme, open }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1100, // Por debajo del sidebar pero encima del contenido
  display: open ? 'block' : 'none',
  [theme.breakpoints.up('md')]: {
    display: 'none' // Nunca mostrar en escritorio
  }
}));

const SidebarArea = styled(Box)(({ theme }) => ({
  position: 'absolute', // Mantener como absolute para superponerse en móviles
  left: 0,
  top: 0,
  height: '100%',
  width: '280px',
  zIndex: 1200,
  backgroundColor: theme.palette.background.paper, // Para asegurarnos que es opaco
  boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' // Sombra más visible en móviles
}));

const MainArea = styled(Box)(({ theme, sidebarOpen }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  transition: theme.transitions.create('margin-left', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  // Solo aplicar marginLeft a partir de md breakpoint
  [theme.breakpoints.up('md')]: {
    marginLeft: sidebarOpen ? '280px' : 0,
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  width: '100%',
  overflow: 'auto',
  padding: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3)
  }
}));

const HeaderToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ModuleContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  paddingTop: theme.spacing(1.5),
  width: '100%',
  maxWidth: '950px', // Reducido significativamente
  margin: '0 auto',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5)
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5)
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)', // Fijado a 3 columnas exactas
    gap: theme.spacing(1.5)
  }
}));

// Nueva estructura para el modo chat
const ChatLayoutContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column', // Cambiado a column para mejor control
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
  alignItems: 'center', // Centrar horizontalmente
}));

const ChatMessagesContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '800px', // Límite de ancho para los mensajes
  margin: '0 auto', // Centrar el contenedor
}));

const ChatInputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  width: '100%',
  maxWidth: '800px', // Mismo ancho máximo que los mensajes
  margin: '0 auto', // Centrar el contenedor
}));

const ModuleCard = styled(Paper)(({ theme, color }) => ({
  position: 'relative',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  cursor: 'pointer',
  height: '120px',
  maxWidth: '100%', // Usar todo el ancho disponible de la celda
  width: '100%',
  backgroundColor: color || theme.palette.primary.main,
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)'
  },
  '& .content-wrapper': {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: `linear-gradient(to top, rgba(0,0,0,0.4), transparent)`,
    zIndex: 1
  },
  '& .title-wrapper': {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    zIndex: 2
  },
  '& .title': {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.25rem'
    }
  },
  '& .description': {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '0.8rem',
    lineHeight: 1.2,
    position: 'absolute',
    bottom: theme.spacing(1.5),
    left: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 2,
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.85rem'
    }
  }
}));

const DocumentEditorCard = styled(Paper)(({ theme, colors }) => ({
  position: 'relative',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  cursor: 'pointer',
  height: '120px',
  maxWidth: '100%',
  width: '100%',
  backgroundColor: '#0C0C0C', // Fondo oscuro para que los cuadrados resalten
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)'
  },
  '& .ms-logo': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0.7,
    transform: 'rotate(-15deg) scale(1.4) translateX(10%) translateY(-5%)', // Rotación diagonal
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '40%',
      left: '50%',
      width: '60px',
      height: '60px',
      background: `
        linear-gradient(135deg, #F25022 0%, #F25022 49%, transparent 51%), 
        linear-gradient(135deg, #7FBA00 0%, #7FBA00 49%, transparent 51%), 
        linear-gradient(135deg, #00A4EF 0%, #00A4EF 49%, transparent 51%), 
        linear-gradient(135deg, #FFB900 0%, #FFB900 49%, transparent 51%)
      `,
      backgroundPosition: '0 0, 100% 0, 0 100%, 100% 100%',
      backgroundSize: '50% 50%',
      backgroundRepeat: 'no-repeat',
      transform: 'translate(-50%, -50%)',
      boxShadow: '0 0 30px rgba(0,0,0,0.2)',
      zIndex: 0,
      filter: 'blur(5px)'
    }
  },
  '& .content-wrapper': {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: `linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.1))`,
    zIndex: 1
  },
  '& .title-wrapper': {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    zIndex: 2
  },
  '& .title': {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.25rem'
    }
  },
  '& .description': {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '0.8rem',
    lineHeight: 1.2,
    position: 'absolute',
    bottom: theme.spacing(1.5),
    left: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 2,
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.85rem'
    }
  }
}));

const CircularIconWrapper = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(to top, rgba(0,0,0,0.4), transparent)`,
  borderRadius: '50%',
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  color: '#fff',
  '&:hover': {
    transform: 'scale(1.05)',
    background: alpha(theme.palette.common.white, 0.15)
  }
}));

// ====================== Datos de productos ======================
const products = [
  {
    id: 'directive-ai',
    title: 'DirectiveAI Mode',
    description: 'Real-time group chats with seamless collaboration and AI assistance',
    color: '#4f46e5',
    icon: MessageSquare,
    component: DirectiveAI,
    notifications: 3
  },
  {
    id: 'document-editor',
    title: 'AGT Microsoft Office',
    description: 'Edit documents with AGT assistance to improve content and formatting',
    icon: FileEdit,
    component: null, // No necesitamos un componente específico aquí
    notifications: 0
  },
  {
    id: 'academic',
    title: 'AGT Academic',
    description: 'Advanced research tools, citation generation, and project planning',
    color: '#0891b2',
    icon: GraduationCap,
    component: AGTAcademic,
    notifications: 2
  },
  {
    id: 'growth',
    title: 'AGT Growth',
    description: 'Tailored learning strategies for every educational level',
    color: '#059669',
    icon: TrendingUp,
    notifications: 1,
    component: AGTGrowth
  }
];

// ====================== Componente principal ======================
const AGTEdu = () => {
  const theme = useTheme();
  const colors = navigationColors(theme);
  const messagesEndRef = useRef(null);
  
  // Tiempo mínimo para mostrar el indicador thinking - DEBE coincidir con el valor en ChatInputMUI
  const MIN_THINKING_DISPLAY_TIME = 1500; // ms
  
  // Estados
  const [activeProduct, setActiveProduct] = useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [favorites, setFavorites] = useState([]);
  
  // Nuevos estados para el modo chat
  const [chatMode, setChatMode] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // NUEVO ESTADO PARA EDITOR DE DOCUMENTOS
  const [documentEditorMode, setDocumentEditorMode] = useState(false);
  
  // NUEVO ESTADO PARA CONTROLAR LA VISIBILIDAD DEL HEADER
  const [headerVisible, setHeaderVisible] = useState(true);

  // Manejar cambios de tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        // No abrir automáticamente el sidebar si estamos en modo editor de documentos
        if (!documentEditorMode) {
          setSidebarOpen(true);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Ejecutar al inicio
    
    return () => window.removeEventListener('resize', handleResize);
  }, [documentEditorMode]);

  // Efecto para hacer scroll al final cuando se cargan los mensajes
  useEffect(() => {
    // Cuando se terminen de cargar los mensajes de una conversación
    if (!messagesLoading && messages.length > 0) {
      setTimeout(() => {
        // Intentar hacer scroll de dos maneras diferentes para mayor seguridad
        
        // 1. Buscar el contenedor de mensajes por su clase
        const chatContainer = document.querySelector('.ChatMessagesContainer');
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
        
        // 2. Usar la referencia al elemento final si está disponible
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
        }
      }, 200); // Un retraso mayor para asegurar que todo el DOM está renderizado
    }
  }, [messagesLoading, messages]);

  // Cargar conversaciones del usuario desde el backend
  const fetchConversations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await agtEduService.getConversations();
      
      if (response.success && response.data) {
        // Ordenar conversaciones por timestamp (más recientes primero)
        const sortedConversations = response.data.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        setConversations(sortedConversations);
      } else {
        console.warn('No se pudieron cargar las conversaciones:', response);
        setSnackbarMessage('No se pudieron cargar las conversaciones');
        setSnackbarSeverity('warning');
        setShowSnackbar(true);
      }
    } catch (error) {
      console.error('Error al cargar conversaciones:', error);
      setSnackbarMessage('Error al cargar conversaciones');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cargar conversaciones al montar el componente
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Nueva función para manejar el editor de documentos con ocultación de sidebar y header
  const handleDocumentEditorClick = useCallback(() => {
    setDocumentEditorMode(true);
    setChatMode(false);
    setActiveProduct(null);
    
    // Ocultar siempre el sidebar cuando se active el editor de documentos
    setSidebarOpen(false);
    
    // Ocultar el header
    setHeaderVisible(false);
  }, []);

  // Función para volver desde el editor de documentos y restaurar sidebar y header
  const handleBackFromDocumentEditor = useCallback(() => {
    setDocumentEditorMode(false);
    
    // Restaurar el header
    setHeaderVisible(true);
    
    // Restaurar el sidebar solo en dispositivos de escritorio
    if (!isMobile) {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  // Modificar handleProductClick para manejar también el editor de documentos
  const handleProductClick = useCallback((productId) => {
    if (productId === 'document-editor') {
      handleDocumentEditorClick();
    } else {
      setActiveProduct(productId);
      setDocumentEditorMode(false);
      
      // Asegurar que el header esté visible para otros productos
      setHeaderVisible(true);
    }
  }, [handleDocumentEditorClick]);

  const handleBack = useCallback(() => {
    setActiveProduct(null);
    setDocumentEditorMode(false);
    
    // Restaurar el header al volver
    setHeaderVisible(true);
    
    // Restaurar el sidebar solo en dispositivos de escritorio
    if (!isMobile) {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const toggleFavorite = useCallback((productId) => {
    setFavorites((prev) => {
      const newFavorites = prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId];

      setSnackbarMessage(
        prev.includes(productId) ? 'Removed from favorites' : 'Added to favorites'
      );
      setSnackbarSeverity('success');
      setShowSnackbar(true);

      return newFavorites;
    });
  }, []);

  // Función para manejar la apertura de la biblioteca
  const handleOpenLibrary = useCallback(() => {
    // En dispositivos móviles, cerrar el sidebar
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  // Función para manejar el envío de un nuevo mensaje - ACTUALIZADA para manejar correctamente el ThinkingIcon
  const handleMessageSent = useCallback((responseData, subject) => {
    // Verificar si tenemos datos válidos
    if (responseData && responseData.success && responseData.data) {
      // Comprobar si es un mensaje temporal del usuario solamente
      if (responseData.data.tempUserMessageOnly) {
        // Es solo un mensaje temporal del usuario, lo añadimos a los mensajes
        const { userMessage, thinkingMessage } = responseData.data;
        
        // Actualizar mensajes con el mensaje del usuario Y el mensaje thinking
        setMessages(prevMessages => [...prevMessages, userMessage, thinkingMessage]);
        
        // Si no estamos en modo chat, cambiar a modo chat
        if (!chatMode) {
          setChatMode(true);
        }
        
        // Hacer scroll al final después de añadir el mensaje del usuario
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
          
          const chatContainer = document.querySelector('.ChatMessagesContainer');
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
        
        return; // Salir temprano, no actualizamos conversaciones todavía
      }
      
      // Si llegamos aquí, es una respuesta completa del servidor
      const { userMessage, assistantMessage, conversation } = responseData.data;
      
      // Obtener el tiempo que ha pasado desde que se mostró el indicador thinking
      const thinkingStartTime = responseData.data.thinkingStartTime || Date.now() - MIN_THINKING_DISPLAY_TIME;
      const thinkingDuration = Date.now() - thinkingStartTime;
      
      // Función para procesar la respuesta
      const processResponse = () => {
        // Actualizar los mensajes manteniendo el orden cronológico (antiguos primero)
        setMessages(prevMessages => {
          // Intentar encontrar un mensaje thinking relacionado con este mensaje
          const relevantThinkingMsg = prevMessages.find(msg => 
            msg.role === 'thinking' && 
            (msg.metadata?.relatedToMessage === userMessage.text || 
             (Date.now() - new Date(msg.createdAt).getTime() < 30000))
          );
          
          if (relevantThinkingMsg) {
            // Si encontramos un mensaje thinking relevante, reemplazarlo con el mensaje del asistente
            return prevMessages.map(msg => {
              if (msg.id === relevantThinkingMsg.id) {
                return assistantMessage;
              }
              
              // También actualizar cualquier mensaje temporal del usuario
              if ((msg.id === userMessage.id) || 
                  (msg.id.startsWith('temp-') && msg.text === userMessage.text)) {
                return {
                  ...userMessage,
                  // Mantenemos la ID del mensaje real del servidor
                  id: userMessage.id || msg.id
                };
              }
              
              return msg;
            });
          } else {
            // Si no hay mensaje thinking relevante, limpiar mensajes thinking antiguos
            // y añadir el mensaje del asistente al final
            const updatedMessages = prevMessages
              .filter(msg => {
                // Mantener todos los mensajes que no son thinking antiguos
                if (msg.role !== 'thinking') return true;
                
                // Mantener thinking recientes (menos de 30 segundos)
                const msgTime = new Date(msg.createdAt).getTime();
                return (Date.now() - msgTime) < 30000;
              })
              .map(msg => {
                // Reemplazar cualquier mensaje temporal con el mensaje real del usuario
                if ((msg.id === userMessage.id) || 
                    (msg.id.startsWith('temp-') && msg.text === userMessage.text)) {
                  return {
                    ...userMessage,
                    // Mantenemos la ID del mensaje real del servidor
                    id: userMessage.id || msg.id
                  };
                }
                return msg;
              });
            
            // Añadir el mensaje del asistente al final
            return [...updatedMessages, assistantMessage];
          }
        });
        
        // Hacer scroll al final después de obtener la respuesta
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
          
          const chatContainer = document.querySelector('.ChatMessagesContainer');
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
        
        // Si no estamos en modo chat, cambiar a modo chat
        if (!chatMode) {
          setChatMode(true);
        }
        
        // Manejar la conversación
        if (conversation) {
          // Si la respuesta incluye la conversación actualizada/creada
          setCurrentConversation(conversation);
          
          // Actualizar la lista de conversaciones
          setConversations(prev => {
            // Verificar si la conversación ya existe
            const exists = prev.some(c => c.id === conversation.id);
            
            if (exists) {
              // Actualizar la conversación existente
              return prev.map(c => 
                c.id === conversation.id ? conversation : c
              ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            } else {
              // Agregar la nueva conversación al inicio
              return [conversation, ...prev];
            }
          });
        } else if (currentConversation) {
          // Actualizar la conversación actual si no viene en la respuesta
          const updatedConversation = {
            ...currentConversation,
            lastMessage: userMessage.text.substring(0, 100),
            timestamp: new Date().toISOString()
          };
          
          setCurrentConversation(updatedConversation);
          
          // Actualizar la lista de conversaciones
          setConversations(prev => 
            prev.map(c => c.id === currentConversation.id ? updatedConversation : c)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          );
        }
      };
      
      // Asegurar que el indicador thinking se muestre al menos por un tiempo mínimo
      if (thinkingDuration < MIN_THINKING_DISPLAY_TIME) {
        // Si ha pasado menos tiempo que el mínimo, esperar un poco más
        setTimeout(processResponse, MIN_THINKING_DISPLAY_TIME - thinkingDuration);
      } else {
        // Si ya ha pasado suficiente tiempo, procesar inmediatamente
        processResponse();
      }
    }
  }, [chatMode, currentConversation, MIN_THINKING_DISPLAY_TIME]);

  // Función para iniciar una nueva conversación
  const handleNewChat = useCallback(() => {
    // En vez de mantener el modo chat activo, lo desactivamos para mostrar el landing
    setChatMode(false);
    setCurrentConversation(null);
    setMessages([]);
  }, []);

  // Función para seleccionar una conversación existente
  const handleSelectConversation = useCallback(async (conversation) => {
    try {
      setCurrentConversation(conversation);
      setMessagesLoading(true);
      
      // Limpiar mensajes inmediatamente para evitar mezclas
      setMessages([]);
      
      // Cargar mensajes de la conversación desde el backend
      const response = await agtEduService.getConversationMessages(conversation.id);
      
      if (response.success && response.data) {
        // Asegurarnos de que los mensajes estén ordenados cronológicamente
        const sortedMessages = response.data.sort((a, b) => 
          new Date(a.createdAt) - new Date(b.createdAt)
        );
        
        setMessages(sortedMessages);
        
        // Añadir un pequeño retraso para asegurar que el DOM se actualiza
        setTimeout(() => {
          // Intentar hacer scroll hasta el final de dos maneras diferentes
          const chatContainer = document.querySelector('.ChatMessagesContainer');
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
          
          // También usar la referencia si está disponible
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
          }
        }, 200);
      } else {
        console.warn('No se pudieron cargar los mensajes:', response);
        setMessages([]);
        setSnackbarMessage('No se pudieron cargar los mensajes');
        setSnackbarSeverity('warning');
        setShowSnackbar(true);
      }
      
      setChatMode(true);
      
      if (isMobile) {
        setSidebarOpen(false);
      }
    } catch (error) {
      console.error('Error al cargar los mensajes de la conversación:', error);
      setMessages([]);
      setSnackbarMessage('Error al cargar los mensajes');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    } finally {
      setMessagesLoading(false);
    }
  }, [isMobile]);

  // Función para eliminar una conversación
  const handleDeleteConversation = useCallback(async (conversationId) => {
    try {
      const response = await agtEduService.deleteConversation(conversationId);
      
      if (response.success) {
        // Actualizar lista de conversaciones
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        
        // Si es la conversación actual, cerrarla
        if (currentConversation && currentConversation.id === conversationId) {
          handleNewChat();
        }
        
        setSnackbarMessage('Conversación eliminada');
        setSnackbarSeverity('success');
        setShowSnackbar(true);
      } else {
        console.warn('No se pudo eliminar la conversación:', response);
        setSnackbarMessage('No se pudo eliminar la conversación');
        setSnackbarSeverity('warning');
        setShowSnackbar(true);
      }
    } catch (error) {
      console.error('Error al eliminar la conversación:', error);
      setSnackbarMessage('Error al eliminar la conversación');
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    }
  }, [currentConversation, handleNewChat]);

  // Función para alternar la visibilidad del sidebar
  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Estructura principal con sidebar persistente
  return (
    <AppContainer>
      {/* Overlay para móviles cuando el sidebar está abierto */}
      <MobileOverlay 
        open={sidebarOpen && isMobile} 
        onClick={() => setSidebarOpen(false)} 
      />
      
      {/* Sidebar con visibilidad controlada por sidebarOpen */}
      {sidebarOpen && (
        <SidebarArea>
          <ChatSidebar 
            open={true} // siempre abierto dentro de su contenedor
            conversations={conversations}
            currentConversationId={currentConversation?.id}
            onSelectConversation={handleSelectConversation}
            onDeleteConversation={handleDeleteConversation}
            onNewChat={handleNewChat}
            onClose={() => setSidebarOpen(false)}
            onOpenLibrary={handleOpenLibrary}
          />
        </SidebarArea>
      )}
      
      {/* Área principal */}
      <MainArea sidebarOpen={sidebarOpen}>
        {/* Header con botón de toggle sidebar - condicionado por headerVisible */}
        {headerVisible && (
          <HeaderToolbar>
            <IconButton onClick={toggleSidebar} sx={{ mr: 1 }}>
              {sidebarOpen ? <X size={20} /> : <MenuIcon size={20} />}
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {documentEditorMode 
                ? 'Editor de Documentos'
                : chatMode 
                  ? (currentConversation?.title || 'Nueva conversación')
                  : 'AGT Edu'
              }
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {chatMode && (
              <IconButton onClick={handleNewChat}>
                <PlusCircle size={20} />
              </IconButton>
            )}
          </HeaderToolbar>
        )}
        
        {/* Contenido basado en el modo */}
        {documentEditorMode ? (
          // Vista del editor de documentos
          <DocumentEditorView onBack={handleBackFromDocumentEditor} />
        ) : chatMode ? (
          // Vista de chat
          <ChatLayoutContainer>
            {/* Área de mensajes */}
            <ChatMessagesContainer className="ChatMessagesContainer">
              {messagesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={40} />
                </Box>
              ) : messages.length > 0 ? (
                // Asegurarnos de que los mensajes estén en orden cronológico
                [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).map((msg) => (
                  msg.role === 'thinking' ? (
                    // Renderizado especial para mensajes thinking - asegurar visibilidad
                    <Box 
                      key={`thinking-container-${msg.id}`} 
                      sx={{ 
                        width: '100%', 
                        mb: 2, 
                        opacity: 1, 
                        visibility: 'visible',
                        display: 'flex',
                        justifyContent: 'flex-start'
                      }}
                    >
                      <ThinkingIcon 
                        startTime={msg.metadata?.thinkingStartTime || (msg.createdAt ? new Date(msg.createdAt).getTime() : Date.now())} 
                        colors={colors}
                        key={`thinking-icon-${msg.id}`} // Forzar remontado con key única
                      />
                    </Box>
                  ) : (
                    // Renderizado normal para mensajes de usuario y asistente
                    <Box 
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        mb: 2,
                        maxWidth: msg.role === 'user' ? '70%' : '100%',
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        width: msg.role === 'assistant' ? '100%' : 'auto',
                      }}
                    >
                      <Paper
                        elevation={0} // Quitamos la elevación para ambos tipos de mensaje
                        sx={{
                          p: 2,
                          backgroundColor: msg.role === 'user' 
                            ? theme.palette.mode === 'light' ? '#E2E2E7' : colors.cardBackground
                            : 'transparent',
                          color: colors.textDefault,
                          borderRadius: msg.role === 'user' ? '28px' : 2, // Mayor redondeo para mensajes del usuario
                          width: msg.role === 'assistant' ? '100%' : 'auto',
                        }}
                      >
                        {msg.role === 'assistant' ? (
                          <MarkdownRenderer 
                            content={msg.text} 
                            isNew={msg.id === messages[messages.length - 1]?.id && msg.role === 'assistant'} 
                            theme={theme}
                            baseDelay={200}
                          />
                        ) : (
                          <Typography>{msg.text}</Typography>
                        )}
                      </Paper>
                    </Box>
                  )
                ))
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    Escribe un mensaje para comenzar...
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} id="messages-end-ref" />
            </ChatMessagesContainer>
            
            {/* Input de chat en la parte inferior */}
            <ChatInputContainer>
              <ChatInputMUI 
                onConversationUpdate={handleMessageSent}
                chatMode={true}
                currentConversation={currentConversation}
              />
            </ChatInputContainer>
          </ChatLayoutContainer>
        ) : (
          <ContentContainer>
            <Fade in timeout={300}>
              <Box>
                {!activeProduct ? (
                  <Box sx={{ maxWidth: '950px', margin: '0 auto' }}>
                    {/* ChatInputMUI arriba de Explore Tools */}
                    <Box sx={{ mb: 3 }}>
                      <ChatInputMUI 
                        onConversationUpdate={handleMessageSent}
                        chatMode={false}
                      />
                    </Box>

                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: 600,
                        position: 'relative',
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          bottom: -8,
                          left: 0,
                          width: 40,
                          height: 3,
                          backgroundColor: 'primary.main',
                          borderRadius: 4
                        }
                      }}
                    >
                      Explore Tools
                    </Typography>
                    <ModuleContainer>
                      {products.map((product) => (
                        product.id === 'document-editor' ? (
                          <DocumentEditorCard
                            key={product.id}
                            onClick={() => handleProductClick(product.id)}
                            elevation={3}
                            colors={colors}
                          >
                            <div className="ms-logo"></div>
                            <div className="title-wrapper">
                              <Typography className="title" variant="h6">
                                {product.title}
                              </Typography>
                              {product.notifications > 0 && (
                                <Badge badgeContent={product.notifications} color="error" />
                              )}
                            </div>
                            <Typography className="description">{product.description}</Typography>
                            <div className="content-wrapper" />
                          </DocumentEditorCard>
                        ) : (
                          <ModuleCard
                            key={product.id}
                            onClick={() => handleProductClick(product.id)}
                            color={product.color}
                            elevation={3}
                          >
                            <div className="title-wrapper">
                              <Typography className="title" variant="h6">
                                {product.title}
                              </Typography>
                              {product.notifications > 0 && (
                                <Badge badgeContent={product.notifications} color="error" />
                              )}
                            </div>
                            <Typography className="description">{product.description}</Typography>
                            <div className="content-wrapper" />
                          </ModuleCard>
                        )
                      ))}
                    </ModuleContainer>
                  </Box>
                ) : (
                  <Box>
                    {(() => {
                      const ProductComponent = products.find(
                        (p) => p.id === activeProduct
                      )?.component;
                      return ProductComponent ? <ProductComponent /> : null;
                    })()}
                    
                    {/* Botón para volver cuando hay producto activo */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <CircularIconWrapper onClick={handleBack}>
                        <ChevronLeft />
                      </CircularIconWrapper>
                      <CircularIconWrapper onClick={() => toggleFavorite(activeProduct)}>
                        <Star fill={favorites.includes(activeProduct) ? 'currentColor' : 'none'} />
                      </CircularIconWrapper>
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          </ContentContainer>
        )}
      </MainArea>

      {/* Notifications Drawer */}
      <Drawer anchor="right" open={notificationsOpen} onClose={() => setNotificationsOpen(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Notifications
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <BookOpen size={20} style={{ color: '#0284C7' }} />
              </ListItemIcon>
              <ListItemText
                primary="New study session invitation"
                secondary="Advanced Mathematics - 5 minutes ago"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <PenTool size={20} style={{ color: '#10B981' }} />
              </ListItemIcon>
              <ListItemText
                primary="Assignment due soon"
                secondary="Physics Report - Due in 2 hours"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <Target size={20} style={{ color: '#6366F1' }} />
              </ListItemIcon>
              <ListItemText
                primary="Learning milestone achieved"
                secondary="Completed Advanced SQL Course"
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Settings Drawer */}
      <Drawer anchor="right" open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Learning Settings
          </Typography>
          <List>
            <ListItemButton>
              <ListItemIcon>
                <Calendar size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Study Schedule"
                secondary="Manage your study times"
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <BookMarked size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Learning Preferences"
                secondary="Customize your learning style"
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <Bell size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Notifications"
                secondary="Set reminder preferences"
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <Users size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Study Groups"
                secondary="Manage group settings"
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemIcon>
                <FileText size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Progress Reports"
                secondary="Configure report settings"
              />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AppContainer>
  );
};

export default AGTEdu;